import * as yup from 'yup'

export enum BillBankType {
  CHECKING = 'CHECKING',
  SAVINGS = 'SAVINGS',
}

export enum BillOwnerType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

export enum BillVoidPaymentType {
  VOID_AND_CREDIT = 'VOID_AND_CREDIT',
  VOID_AND_REISSUE = 'VOID_AND_REISSUE',
  VOID_ONLY = 'VOID_ONLY',
  VOID_AND_REISSUE_VCARD = 'VOID_AND_REISSUE_VCARD',
}

export enum BillRequestCheckDeliveryType {
  STANDARD = 'STANDARD',
  USPS_PRIORITY = 'USPS_PRIORITY',
  UPS_1DAY = 'UPS_1DAY',
  UPS_2DAY = 'UPS_2DAY',
  UPS_3DAY = 'UPS_3DAY',
  RTP_DELIVERY = 'RTP_DELIVERY',
}

export const dossBillPayPhoneNumberSchema = yup.object({
  phone: yup.string().required(),
})
export type DossBillPayPhoneNumber = yup.InferType<typeof dossBillPayPhoneNumberSchema>

export const dossBillPay2FACodeSchema = yup.object({
  code: yup.string().required(),
})
export type DossBillPay2FACode = yup.InferType<typeof dossBillPay2FACodeSchema>

export const dossBillPayFundingAccountSchema = yup.object({
  code: yup.string().required(),
  nameOnAccount: yup.string().required(),
  routingNumber: yup.string().required(),
  accountNumber: yup.string().required(),
  type: yup.string().oneOf(Object.values(BillBankType)).required(),
  ownerType: yup.string().oneOf(Object.values(BillOwnerType)).required(),
  bankName: yup.string().required(),
})
export type BillFundingAccountMetadata = yup.InferType<typeof dossBillPayFundingAccountSchema>

export enum TwoFactorPhase {
  SETUP_PHONE_NUMBER = 'SETUP_PHONE_NUMBER',
  CONFIRM_PHONE_NUMBER = 'CONFIRM_PHONE_NUMBER',
  SETUP_BANK_DETAILS = 'SETUP_BANK_DETAILS',
  CONFIRM_BANK_DETAILS = 'CONFIRM_BANK_DETAILS',
}

// Doss Bill Pay Payment Updated Webhook Schema
export const dossBillPayPaymentUpdatedSchema = yup.object().shape({
  metadata: yup
    .object()
    .shape({
      eventId: yup.string().notRequired(),
      subscriptionId: yup.string().notRequired(),
      organizationId: yup.string().notRequired(),
      eventType: yup.string().notRequired(),
      version: yup.string().notRequired(),
    })
    .notRequired(),
  payment: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      billIds: yup.array().of(yup.string().notRequired()).notRequired(),
      transactionNumber: yup.string().notRequired(),
      status: yup.string().notRequired(),
      createdBy: yup.string().notRequired(),
      createdTime: yup.string().notRequired(),
      updatedTime: yup.string().notRequired(),
      processDate: yup.string().notRequired(),
      funding: yup
        .object()
        .shape({
          amount: yup.number().notRequired(),
          currency: yup.string().notRequired(),
          fundingAccount: yup
            .object()
            .shape({
              id: yup.string().notRequired(),
              type: yup.string().notRequired(),
              name: yup.string().notRequired(),
              accountNumber: yup.string().notRequired(),
            })
            .notRequired(),
        })
        .notRequired(),
      disbursement: yup
        .object()
        .shape({
          amount: yup.number().notRequired(),
          currency: yup.string().notRequired(),
          sentDate: yup.string().notRequired(),
          arrivesByDate: yup.string().notRequired(),
          checkNumber: yup.string().notRequired(),
          disbursementAccount: yup
            .object()
            .shape({
              type: yup.string().notRequired(),
              accountNumber: yup.string().notRequired(),
            })
            .notRequired(),
        })
        .notRequired(),
      vendor: yup
        .object()
        .shape({
          id: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .notRequired(),
      billingType: yup.string().notRequired(),
    })
    .notRequired(),
})

// Doss Bill Pay Payment Failed Webhook Schema
export const dossBillPayPaymentFailedSchema = yup.object().shape({
  metadata: yup
    .object()
    .shape({
      eventId: yup.string().notRequired(),
      subscriptionId: yup.string().notRequired(),
      organizationId: yup.string().notRequired(),
      eventType: yup.string().notRequired(),
      version: yup.string().notRequired(),
    })
    .notRequired(),
  payment: yup
    .object()
    .shape({
      bills: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              id: yup.string().notRequired(),
              amount: yup.number().notRequired(),
            })
            .notRequired(),
        )
        .notRequired(),
      transactionNumber: yup.string().notRequired(),
      createdBy: yup.string().notRequired(),
      createdTime: yup.string().notRequired(),
      updatedTime: yup.string().notRequired(),
      fundingAccount: yup
        .object()
        .shape({
          id: yup.string().notRequired(),
          type: yup.string().notRequired(),
        })
        .notRequired(),
      vendor: yup
        .object()
        .shape({
          id: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .notRequired(),
    })
    .notRequired(),
  errors: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          code: yup.string().notRequired(),
          message: yup.string().notRequired(),
          timestamp: yup.string().notRequired(),
        })
        .notRequired(),
    )
    .notRequired(),
})

const billVendorCreditsObjectSchema = yup.object().shape({
  id: yup.string(),
  amount: yup.number(),
})

const billPaymentObjectSchema = yup.object().shape({
  id: yup.string(),
  billId: yup.string(),
  amount: yup.number(),
  vendorCredits: yup.array().of(billVendorCreditsObjectSchema).notRequired(),
})

const billVoidInfoObjectSchema = yup.object().shape({
  id: yup.string(),
  requestedBy: yup.string(),
  type: yup.string(),
  status: yup.string(),
  reason: yup.string(),
  createdTime: yup.string(),
  updatedTime: yup.string(),
})

export const billPayCreatePaymentResponseSchema = yup.object().shape({
  id: yup.string().required(),
  vendorId: yup.string(),
  vendorName: yup.string(),
  billId: yup.string(),
  billPayments: yup.array().of(billPaymentObjectSchema),
  description: yup.string(),
  processDate: yup.string(),
  fundingAccount: yup.object().shape({
    id: yup.string(),
    type: yup.string(),
  }),
  amount: yup.number().required(),
  processingOptions: yup
    .object()
    .shape({
      requestPayFaster: yup.boolean(),
      createBill: yup.boolean(),
      requestCheckDeliveryType: yup.string(),
    })
    .notRequired(),
  transactionNumber: yup.string(),
  confirmationNumber: yup.string(),
  status: yup.string(),
  exchangeRate: yup.number(),
  fundingAmount: yup.number(),
  onlinePayment: yup.boolean(),
  disbursementType: yup.string(),
  remittanceEmail: yup.string(),
  createdTime: yup.string().notRequired(),
  updatedTime: yup.string().notRequired(),
  createdBy: yup.string(),
  voidInfo: yup.array().of(billVoidInfoObjectSchema).notRequired(),
  disbursementStatus: yup.string().notRequired(),
  disbursementInfo: yup
    .object()
    .shape({
      isVoidable: yup.boolean(),
      sentDate: yup.string(),
      arrivesByDate: yup.string(),
      checkNumber: yup.number(),
      traceNumber: yup.number(),
      batchNumber: yup.number(),
      amount: yup.number(),
      disbursementSubType: yup.string(),
      validUntil: yup.string(),
      last4CardNumber: yup.string(),
    })
    .notRequired(),
  paymentPurpose: yup
    .object()
    .shape({
      text: yup.string(),
      code: yup.object().shape({
        name: yup.string(),
        value: yup.string(),
      }),
    })
    .notRequired(),
  cardFundingPurpose: yup.string(),
  cancelRequestSubmitted: yup.boolean(),
  singleStatus: yup.string(),
})
export type BillPayCreatePaymentResponse = yup.InferType<typeof billPayCreatePaymentResponseSchema>

/**
 * Successful Bill.com cancel payment response object
 * https://developer.bill.com/reference/cancelpayment
 */
export const billPayCancelPaymentResponseSchema = yup.object().shape({
  id: yup.string().required(),
  vendorId: yup.string(),
  vendorName: yup.string(),
  billId: yup.string(),
  billPayments: yup.array().of(billPaymentObjectSchema),
  description: yup.string(),
  processDate: yup.string(),
  fundingAccount: yup.object().shape({
    id: yup.string(),
    type: yup.string(),
  }),
  amount: yup.number().required(),
  processingOptions: yup
    .object()
    .shape({
      requestPayFaster: yup.boolean(),
      createBill: yup.boolean(),
      requestCheckDeliveryType: yup.string(),
    })
    .notRequired(),
  transactionNumber: yup.string(),
  confirmationNumber: yup.string(),
  status: yup.string(),
  exchangeRate: yup.number(),
  fundingAmount: yup.number(),
  onlinePayment: yup.boolean(),
  disbursementType: yup.string(),
  remittanceEmail: yup.string(),
  createdTime: yup.string().notRequired(),
  updatedTime: yup.string().notRequired(),
  createdBy: yup.string(),
  voidInfo: yup.array().of(billVoidInfoObjectSchema).notRequired(),
  disbursementStatus: yup.string().notRequired(),
  disbursementInfo: yup
    .object()
    .shape({
      isVoidable: yup.boolean(),
      sentDate: yup.string(),
      arrivesByDate: yup.string(),
      checkNumber: yup.number(),
      traceNumber: yup.number(),
      batchNumber: yup.number(),
      amount: yup.number(),
      disbursementSubType: yup.string(),
      validUntil: yup.string(),
      last4CardNumber: yup.string(),
    })
    .notRequired(),
  paymentPurpose: yup
    .object()
    .shape({
      text: yup.string(),
      code: yup.object().shape({
        name: yup.string(),
        value: yup.string(),
      }),
    })
    .notRequired(),
  cardFundingPurpose: yup.string(),
  cancelRequestSubmitted: yup.boolean(),
  singleStatus: yup.string(),
})
export type BillPayCancelPaymentResponse = yup.InferType<typeof billPayCancelPaymentResponseSchema>

/**
 * Successful Bill.com void payment response object
 * https://developer.bill.com/reference/voidpayment
 */
export const billPayVoidPaymentResponseSchema = yup.object().shape({
  id: yup.string().required(),
  vendorId: yup.string(),
  vendorName: yup.string(),
  billId: yup.string(),
  billPayments: yup.array().of(billPaymentObjectSchema),
  description: yup.string(),
  processDate: yup.string(),
  fundingAccount: yup.object().shape({
    id: yup.string(),
    type: yup.string(),
  }),
  amount: yup.number().required(),
  processingOptions: yup
    .object()
    .shape({
      requestPayFaster: yup.boolean(),
      createBill: yup.boolean(),
      requestCheckDeliveryType: yup.string(),
    })
    .notRequired(),
  transactionNumber: yup.string(),
  confirmationNumber: yup.string(),
  status: yup.string(),
  exchangeRate: yup.number(),
  fundingAmount: yup.number(),
  onlinePayment: yup.boolean(),
  disbursementType: yup.string(),
  remittanceEmail: yup.string(),
  createdTime: yup.string().notRequired(),
  updatedTime: yup.string().notRequired(),
  createdBy: yup.string(),
  voidInfo: yup.array().of(billVoidInfoObjectSchema).notRequired(),
  disbursementStatus: yup.string().notRequired(),
  disbursementInfo: yup
    .object()
    .shape({
      isVoidable: yup.boolean(),
      sentDate: yup.string(),
      arrivesByDate: yup.string(),
      checkNumber: yup.number(),
      traceNumber: yup.number(),
      batchNumber: yup.number(),
      amount: yup.number(),
      disbursementSubType: yup.string(),
      validUntil: yup.string(),
      last4CardNumber: yup.string(),
    })
    .notRequired(),
  paymentPurpose: yup
    .object()
    .shape({
      text: yup.string(),
      code: yup.object().shape({
        name: yup.string(),
        value: yup.string(),
      }),
    })
    .notRequired(),
  cardFundingPurpose: yup.string(),
  cancelRequestSubmitted: yup.boolean(),
  singleStatus: yup.string(),
})
export type BillPayVoidPaymentResponse = yup.InferType<typeof billPayVoidPaymentResponseSchema>

/**
 * Bill.com Vendor Bank account object. Response of:
 * - Create Vendor Bank https://developer.bill.com/reference/createvendorbankaccount
 * - Get Vendor Bank https://developer.bill.com/reference/getvendorbankaccount
 */
export const billPayVendorBankSchema = yup.object().shape({
  nameOnAccount: yup.string(),
  accountNumber: yup.string(),
  routingNumber: yup.string(),
  type: yup.string().oneOf(Object.values(BillBankType)),
  ownerType: yup.string().oneOf(Object.values(BillOwnerType)),
  regulatoryFields: yup.array().of(
    yup.object().shape({
      name: yup.string(),
      value: yup.string(),
    }),
  ),
  paymentCurrency: yup.string(),
  bankInfo: yup.object().shape({
    swiftBIC: yup.string(),
    branchName: yup.string(),
    countryISO: yup.string(),
    nationalBankCode: yup.string(),
    institutionName: yup.string(),
    primaryKey: yup.string(),
    address: yup.object().shape({
      line1: yup.string(),
      line2: yup.string(),
      city: yup.string(),
      stateOrProvince: yup.string(),
      zipOrPostalCode: yup.string(),
      country: yup.string(),
    }),
  }),
})

export type BillPayVendorBank = yup.InferType<typeof billPayVendorBankSchema>

/**
 * Bill.com Check Image Data response object. Response of:
 * - Get Check Image Data https://developer.bill.com/reference/getcheckimagedata
 */
export const billPayCheckImageDataSchema = yup.object().shape({
  results: yup.array().of(
    yup.object().shape({
      downloadLink: yup.string().required(),
    }),
  ),
})

export type BillPayCheckImageData = yup.InferType<typeof billPayCheckImageDataSchema>

/**
 * Schema for getting check image data from Bill.com
 */
export const dossBillPayGetCheckImageDataSchema = yup.object().shape({
  paymentId: yup.string().required('Payment ID is required'),
})
export type DossBillPayGetCheckImageDataSchema = yup.InferType<
  typeof dossBillPayGetCheckImageDataSchema
>
